import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Battery, Clock, Sun, Brain, Thermometer, Cloud, Pill, Smartphone, Gauge, Compass } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { riseFade } from "@/lib/motion";
import PillarSeo from "@/components/seo/PillarSeo";
import ShareButtons from "@/components/blog/ShareButtons";
import BookmarkButton from "@/components/blog/BookmarkButton";
import RelatedArticles from "@/components/blog/RelatedArticles";

const META_TITLE = "MS Fatigue: The Complete Guide (2026)";
const META_DESC =
  "MS fatigue explained: what it feels like, how long it lasts, whether it goes away, medications that help, and the science behind the most disabling MS symptom.";

const FAQS = [
  {
    question: "What is MS fatigue?",
    answer:
      "MS fatigue is a pathological, disproportionate exhaustion caused by multiple sclerosis, distinct from normal tiredness. It is one of the most common and disabling symptoms of MS, reported by roughly 80% of people with the condition, and is driven by inflammation, demyelination, and the extra neural effort required to move signals through damaged pathways.",
  },
  {
    question: "How is MS fatigue different from ordinary tiredness?",
    answer:
      "Ordinary tiredness improves with rest and is proportionate to exertion. MS fatigue is often disproportionate to activity, is not fully relieved by sleep, worsens with heat, and can appear without warning, it is a neurological symptom, not a lifestyle problem.",
  },
  {
    question: "Does MS fatigue ever go away?",
    answer:
      "For most people, MS fatigue is chronic and fluctuating rather than curable, but its impact is meaningfully reducible. Disease-modifying therapy, treating comorbid sleep and mood issues, structured pacing, and, for some, stimulant medication can move fatigue from disabling to manageable.",
  },
  {
    question: "What medications help with MS fatigue?",
    answer:
      "The most-studied options are modafinil, amantadine, and methylphenidate. Evidence is modest and mixed, response is individual, and side effects matter. Newer research has questioned amantadine and modafinil's superiority over placebo, so a trial-and-review approach with a neurologist is the norm.",
  },
  {
    question: "Why does heat make MS fatigue worse?",
    answer:
      "Even a small rise in core temperature slows conduction through demyelinated nerves, this is Uhthoff's phenomenon. It's why a warm room, a hot shower, or exercise can produce a sudden wave of fatigue and other symptoms that resolve on cooling.",
  },
];

const KEEPERS = [
  {
    slug: "ms-fatigue-management-complete-guide",
    title: "MS Fatigue Management: The Complete Guide",
    blurb: "The full playbook, what MS fatigue is, why it happens, and every proven strategy in one place.",
    icon: Compass,
  },
  {
    slug: "what-does-ms-fatigue-feel-like",
    title: "What Does MS Fatigue Actually Feel Like?",
    blurb: "The felt experience of MS fatigue in the words of the people living with it, and why 'tired' is the wrong word.",
    icon: Battery,
  },
  {
    slug: "how-long-does-ms-fatigue-last",
    title: "How Long Does MS Fatigue Last?",
    blurb: "Hours, days, months, forever? What the data says about episode length, waves, and the long-term arc.",
    icon: Clock,
  },
  {
    slug: "does-ms-fatigue-ever-go-away",
    title: "Does MS Fatigue Ever Go Away?",
    blurb: "The honest answer, plus what people who feel meaningfully better did to get there.",
    icon: Sun,
  },
  {
    slug: "best-medication-for-ms-fatigue",
    title: "The Best Medications for MS Fatigue",
    blurb: "Modafinil, amantadine, methylphenidate, off-label options, what works, what doesn't, and what to ask your neurologist.",
    icon: Pill,
  },
  {
    slug: "brain-fog-ms-cognitive-fatigue",
    title: "MS Brain Fog & Cognitive Fatigue",
    blurb: "The cognitive tax nobody warned you about, and how to spend less on thinking so you have more left for living.",
    icon: Brain,
  },
  {
    slug: "ms-heat-sensitivity-energy",
    title: "MS Heat Intolerance & Uhthoff's Phenomenon",
    blurb: "Why a warm room can flatten you, and the cooling protocols that actually protect your day.",
    icon: Thermometer,
  },
  {
    slug: "weather-changes-ms-fatigue",
    title: "Barometric Pressure & MS Fatigue",
    blurb: "How to read barometric pressure as one signal in your pattern, so you can plan the day around it with intention.",
    icon: Cloud,
  },
  {
    slug: "spoon-theory-vs-energy-budgeting-ms",
    title: "Spoon Theory vs. Energy Budgeting",
    blurb: "Why spoons are a great metaphor but a lousy planning tool, and what to use instead.",
    icon: Gauge,
  },
  {
    slug: "best-ms-tracking-apps-2026",
    title: "The Best MS Tracking Apps (2026)",
    blurb: "The honest comparison of every MS-specific and general fatigue tracker, what to pick, what to skip.",
    icon: Smartphone,
  },
];

const MsFatiguePillar = () => {
  return (
    <>
      <PillarSeo
        slug="blog/pillar/ms-fatigue"
        title={META_TITLE}
        description={META_DESC}
        faqs={FAQS}
        date="2026-07-15"
      />
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-32 pb-20">
          {/* Header */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-muted-foreground hover:text-primary transition-colors mb-10"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to library
            </Link>
            <motion.div initial="hidden" animate="visible" variants={riseFade} className="space-y-6">
              <div className="eyebrow">Pillar guide · MS fatigue</div>
              <h1 className="font-display text-foreground text-display-xl leading-[1.05]">
                MS fatigue,{" "}
                <span className="italic text-primary">the complete guide</span>.
              </h1>
              <p className="text-lg leading-[1.7] text-muted-foreground max-w-2xl">
                What MS fatigue actually is, how it feels, how long it lasts, whether it goes
                away, and what genuinely helps. Ten guides on the single most disabling symptom
                of multiple sclerosis.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link to="/demo">
                  <Button variant="premium" size="lg" className="group w-full sm:w-auto px-8">
                    See the demo
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <a href="#guides">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-8">
                    Jump to the guides
                  </Button>
                </a>
              </div>
              <ShareButtons slug="blog/pillar/ms-fatigue" title={META_TITLE} summary={META_DESC} className="justify-center mt-6" />
              <div className="flex justify-center mt-3">
                <BookmarkButton slug="blog/pillar/ms-fatigue" title="MS Fatigue Guide" category="Pillar" kind="pillar" />
              </div>
            </motion.div>
            <div className="hairline-fade mt-12" />
          </section>

          {/* Explainer */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl pt-16">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-6">
              The symptom, in plain language
            </h2>
            <div className="space-y-6 text-foreground/80 leading-[1.75] font-light text-lg">
              <p>
                Roughly four in five people with multiple sclerosis report fatigue, and about half
                call it their most disabling symptom. It is not laziness, not deconditioning, not
                depression in disguise, although any of those can sit alongside it. It is a
                neurological consequence of MS itself.
              </p>
              <p>
                <strong>What it feels like.</strong> A sudden, disproportionate flattening, often
                mid-morning or after mild exertion, that sleep does not fully repair. Read{" "}
                <Link to="/blog/what-does-ms-fatigue-feel-like">what MS fatigue actually feels like</Link>{" "}
                for the descriptions people use when the word "tired" isn't enough.
              </p>
              <p>
                <strong>How long it lasts.</strong> Anywhere from hours to weeks, in waves. See{" "}
                <Link to="/blog/how-long-does-ms-fatigue-last">how long MS fatigue lasts</Link>{" "}
                for typical episode arcs and{" "}
                <Link to="/blog/does-ms-fatigue-ever-go-away">whether it ever fully goes away</Link>.
              </p>
              <p>
                <strong>What makes it worse.</strong> Heat is the biggest amplifier, see{" "}
                <Link to="/blog/ms-heat-sensitivity-energy">MS heat intolerance</Link>{" "}
                and{" "}
                <Link to="/blog/weather-changes-ms-fatigue">how barometric pressure shows up in your pattern</Link>. Cognitive
                load is the second, see{" "}
                <Link to="/blog/brain-fog-ms-cognitive-fatigue">brain fog and cognitive fatigue</Link>.
              </p>
              <p>
                <strong>What helps.</strong> Medication for some, see{" "}
                <Link to="/blog/best-medication-for-ms-fatigue">the best medications for MS fatigue</Link>.
                Structured pacing and tracking for nearly everyone, see the{" "}
                <Link to="/blog/ms-fatigue-management-complete-guide">complete management guide</Link>,{" "}
                <Link to="/blog/spoon-theory-vs-energy-budgeting-ms">energy budgeting</Link>, and the{" "}
                <Link to="/blog/best-ms-tracking-apps-2026">best MS tracking apps for 2026</Link>.
              </p>
              <p>
                If you want the operational side, how to actually run a week around this, the
                companion pillar,{" "}
                <Link to="/blog/pillar/energy-management-ms">MS energy management</Link>, covers
                predicting, protecting, and spending the energy you do have.
              </p>
            </div>
          </section>

          {/* Keeper index */}
          <section id="guides" className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-5xl pt-20">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="eyebrow justify-center mx-auto mb-4">The library</div>
              <h2 className="font-display text-3xl sm:text-4xl text-foreground">
                Ten guides on MS fatigue
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                Read in any order. Each one links back here.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {KEEPERS.map(({ slug, title, blurb, icon: Icon }, i) => (
                <motion.div
                  key={slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.03 }}
                >
                  <Link to={`/blog/${slug}`} className="group block h-full">
                    <Card className="h-full p-6 transition-all duration-300 hover:shadow-[0_20px_60px_-30px_hsl(var(--foreground)/0.25)] hover:-translate-y-0.5">
                      <div className="flex items-start gap-4">
                        <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/8 text-primary flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-display text-xl text-foreground leading-snug group-hover:text-primary transition-colors">
                            {title}
                          </h3>
                          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{blurb}</p>
                          <span className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-primary/80 group-hover:text-primary">
                            Read guide <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-20 max-w-3xl" aria-labelledby="pillar-fatigue-faq">
            <h2 id="pillar-fatigue-faq" className="font-display text-3xl sm:text-4xl text-foreground text-center mb-10">
              Frequently asked questions
            </h2>
            <div className="space-y-8">
              {FAQS.map((f) => (
                <div key={f.question}>
                  <h3 className="font-display text-xl text-foreground mb-2">{f.question}</h3>
                  <p className="text-foreground/80 leading-[1.75] font-light">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <RelatedArticles topics={["ms fatigue", "lassitude", "heat sensitivity", "brain fog"]} featuredSlugs={["what-does-ms-fatigue-feel-like", "how-long-does-ms-fatigue-last", "best-medication-for-ms-fatigue"]} />
          {/* CTA */}
          <section className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
            <Card className="p-10 text-center bg-primary/5 border-primary/10">
              <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-4">
                Track it, and it stops running your week
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-6">
                A 60-second daily check-in turns your own signals into a pattern you can read and plan around.
                By invitation. No wearable required.
              </p>
              <Link to="/demo">
                <Button variant="premium" size="lg" className="group px-8">
                  See the demo
                  <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </Card>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default MsFatiguePillar;
